// run-command-json-result.ts — 06k C4: object `result` của `run --json` (một dòng
// stdout) và của `last-run.json`. Nhãn/gate chờ đọc lại qua cùng nguồn `status`
// dùng (DRY); field vắng ⇒ bỏ (không `null`); exit_code = runCommandExitCode.
// `stall_reason` qua redactDecisionText (known-values); `gate_blocked` bỏ `reason`.
import { redactDecisionText } from "./decision-policy-journal-events.js";
import { readGateFile } from "./gate-registry.js";
import { knownValuesFor } from "./mission-secrets-test-env-loader.js";
import { missionStatusLabelForReleaseFlow } from "./release-flow-resume-observability.js";
import { runCommandExitCode } from "./run-command-outcome-observability.js";
import { listPendingGateRequests } from "./status-operating-table.js";
import { readStatusSnapshot } from "./status-renderer.js";
import type { RunMissionResult } from "./supervisor-mission-run.js";

export interface RunJsonResult {
  mission_id: string;
  outcome: string;
  label: string;
  exit_code: number;
  pending_gates: Array<{ gate: string; request_id: string }>;
  resume_at?: string;
  wake_at?: string;
  before_dispatch_code?: string;
  gate_blocked?: { ticket_id: string; code: string };
  stall_reason: string[];
  release_label?: string;
  ended_at: string;
}

export interface BuildRunJsonResultInput {
  missionId: string;
  result: RunMissionResult;
  controlRoot: string;
  missionWorkspace?: string;
  now?: () => Date;
}

async function missionLabel(input: BuildRunJsonResultInput): Promise<string | undefined> {
  try {
    const snap = await readStatusSnapshot(
      input.controlRoot,
      input.missionWorkspace,
      input.missionId,
    );
    return "mission_summary" in snap ? snap.mission_summary.status : snap.status;
  } catch {
    return undefined;
  }
}

export async function buildRunJsonResult(input: BuildRunJsonResultInput): Promise<RunJsonResult> {
  const { result } = input;
  const secrets = await knownValuesFor(input.missionId);
  const pending = listPendingGateRequests(await readGateFile(input.controlRoot)).flatMap((p) =>
    p.request_id === undefined ? [] : [{ gate: p.gate, request_id: p.request_id }],
  );
  return {
    mission_id: input.missionId,
    outcome: result.outcome,
    label: (await missionLabel(input)) ?? result.outcome,
    exit_code: runCommandExitCode(result),
    pending_gates: pending,
    ...(result.resumeAt === undefined ? {} : { resume_at: result.resumeAt }),
    ...(result.wakeAt === undefined ? {} : { wake_at: result.wakeAt }),
    ...(result.beforeDispatchCode === undefined
      ? {}
      : { before_dispatch_code: result.beforeDispatchCode }),
    ...(result.gateBlocked === undefined
      ? {}
      : {
          gate_blocked: { ticket_id: result.gateBlocked.ticketId, code: result.gateBlocked.code },
        }),
    stall_reason: (result.stallReason ?? []).map((line) => redactDecisionText(line, secrets)),
    ...(result.releaseFlow === undefined
      ? {}
      : { release_label: missionStatusLabelForReleaseFlow(result.releaseFlow) }),
    ended_at: (input.now ?? (() => new Date()))().toISOString(),
  };
}
